// src/components/organisms/HorizontalScrollCarousel.jsx
import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef } from "react";
import { FiArrowRight } from "react-icons/fi";
import { IconCustom } from "../atoms/IconCustom";
import useMobile from "../../hooks/useMobile";
import { VideoCustom } from "./VideoCustom";
import { HeadingCustom } from "../atoms/HeadingCustom";
import { ParagraphCustom } from "../atoms/ParagraphCustom";
import { Article } from "../atoms/Article";

export const HorizontalScrollCarousel = () => {
  const targetRef = useRef(null);
  const isMobile = useMobile();
  const videoRefs = useRef({});

  const { scrollYProgress } = useScroll({ target: targetRef });
  const x = useTransform(scrollYProgress, [0, 1], isMobile ? ["0%", "0%"] : ["1%", "-75%"]);

  useEffect(() => {
    if (!isMobile) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const { target, isIntersecting } = entry;
          if (target.tagName === "VIDEO") {
            if (isIntersecting) {
              target.play().catch(() => {});
            } else {
              target.pause();
            }
          }
        });
      },
      { threshold: 0.7 }
    );

    const videos = document.querySelectorAll("video");
    videos.forEach((video) => observer.observe(video));
    return () => observer.disconnect();
  }, [isMobile]);

  const renderVideo = (videoName, index) => {
    return (
      <div
        key={videoName}
        className="w-60 lg:h-[30rem] lg:w-80 2xl:h-[60rem] h-96 rounded-sm overflow-hidden"
      >
        <VideoCustom
          videoRef={(el) => (videoRefs.current[`${videoName}_${index}`] = el)}
          videoName={videoName}
        />
      </div>
    );
  };

  return (
    <Article className="relative w-full h-auto bg-slate-50 lg:pt-0 py-10">
      <div ref={targetRef} className="relative lg:h-[700vh] min-h-screen">
        <div className="lg:sticky left-0 top-0 flex lg:h-screen h-full items-center overflow-hidden">
          <motion.ul
            style={{ x, willChange: "transform" }}
            className="flex lg:w-auto w-10/12 mx-auto lg:flex-row flex-col lg:gap-4 gap-20 lg:pl-14 pl-0 items-center"
          >
            {/* Etape 1 */}
            <li className="xl:w-[150vh] lg:w-[110vh] flex lg:flex-row flex-col items-stretch relative gap-10 lg:gap-0">
              <div className="lg:w-1/3 w-full grow relative">
                <div className="xl:max-w-md w-full lg:absolute left-10 bottom-20 flex flex-col gap-2 2xl:gap-8">
                  <HeadingCustom size="large">
                    Influence, musique, création : l’ADN Mobali en 3 temps
                  </HeadingCustom>
                  <ParagraphCustom className="flex flex-col gap-2">
                    Chez Lab Mobali, on ne fait pas que gérer des campagnes :
                    <span className="block">- On les orchestrent avec précision,</span>
                    <span className="block">- On réinvente la musique comme levier d’influence,</span>
                    <span className="block">- On crée dans notre studio Lab, là où les idées prennent forme.</span>
                  </ParagraphCustom>
                  <IconCustom icon="chart" />
                </div>
              </div>
              <div className="lg:w-[60vh] w-full lg:h-screen h-[70vh] overflow-hidden">
                <figure className="lg:h-screen h-[70vh] lg:w-[60vh] w-full">
                  <img
                    src={`${process.env.REACT_APP_URL_CLOUDFARE}images/stud_004.webp`}
                    alt="campagne musical"
                    loading="lazy"
                    decoding="async"
                    width="800"
                    height="800"
                    className="w-full h-full object-cover"
                  />
                </figure>
              </div>
            </li>

            {/* Etape 2 */}
            <li className="lg:w-screen w-full flex flex-col gap-20 items-center relative">
              <div className="lg:w-9/12 w-full flex items-end justify-between gap-10">
                <div className="z-10 flex lg:flex-row flex-col lg:gap-10">
                  <HeadingCustom size="large">Des campagnes qui créent l’impact</HeadingCustom>
                  <ParagraphCustom>
                    Nous connectons les marques aux communautés qui comptent. Stratégies sur mesure, influenceurs authentiques, résultats mesurables.
                  </ParagraphCustom>
                  <div className="order-first">
                    <IconCustom icon="target" />
                  </div>
                </div>
                <span className="lg:block hidden text-4xl text-slate-700">
                  <FiArrowRight />
                </span>
              </div>
              <div className="w-full scrollbar-hidden lg:overflow-auto overflow-x-auto flex gap-4 grow lg:order-first order-last">
                <div className="flex gap-4 items-center justify-center mx-auto whitespace-nowrap">
                  {["impulstar", "lipaulxcamerstyle", "aidemodiale", "baya"].map(renderVideo)}
                </div>
              </div>
            </li>

            {/* Etape 3 */}
            <li className="lg:w-screen w-full flex flex-col lg:flex-row items-stretch">
              <div className="lg:w-fit w-full scrollbar-hidden lg:overflow-auto overflow-x-auto flex gap-4 grow lg:order-first order-last">
                <div className="flex gap-4 items-center justify-center mx-auto whitespace-nowrap">
                  {["stud_video_005", "stud_video_007"].map(renderVideo)}
                </div>
              </div>
              <div className="w-full lg:w-1/2 relative lg:order-last order-first">
                <div className="max-w-md lg:absolute mb-10 lg:mb-0 left-10 bottom-20 flex flex-col">
                  <HeadingCustom size="large">Le Lab : notre studio créatif</HeadingCustom>
                  <ParagraphCustom>
                    Un espace dédié à la création de contenu original, où idées et storytelling prennent vie. Vidéo, photo, design : on s’occupe de tout.
                  </ParagraphCustom>
                  <IconCustom icon="studio" />
                </div>
              </div>
            </li>

            {/* Etape 4 */}
            <li className="lg:w-screen w-full flex lg:flex-row flex-col gap-4 items-center justify-center">
              <div className="lg:w-[60vh] w-full lg:h-screen h-[70vh] overflow-hidden">
                <figure>
                  <img
                    src={`${process.env.REACT_APP_URL_CLOUDFARE}images/bolemvnor2.webp`}
                    alt="campagne musical"
                    loading="lazy"
                    decoding="async"
                    width="800"
                    height="800"
                    className="w-full h-full object-contain"
                  />
                </figure>
              </div>
              <div className="w-full lg:w-1/2 relative lg:order-last order-first">
                <div className="max-w-md flex flex-col">
                  <HeadingCustom size="large">Quand l’influence rime avec musique</HeadingCustom>
                  <ParagraphCustom>
                    Nous produisons, lançons et promouvons des projets musicaux qui amplifient la portée de nos talents et de nos marques partenaires.
                  </ParagraphCustom>
                  <IconCustom icon="music" />
                </div>
              </div>
            </li>
          </motion.ul>
        </div>
      </div>
    </Article>
  );
};